import { createBrowserRouter, Navigate } from "react-router";
import { lazy, Suspense } from "react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ROUTE_NAME } from "@/constants/routes-name";
import Loading from "@/components/Loading";
import { BILLING_ROUTE, KEYS_ROUTE, EVALUATION_ROUTE } from "./index";

// Lazy load pages
const AuthLogin = lazy(() => import("@/pages/AuthLogin/auth-login"));
const AuthForgot = lazy(() => import("@/pages/AuthForgot/auth-forgot"));
const AuthUpdate = lazy(() => import("@/pages/AuthUpdate/auth-update"));
const AuthLogout = lazy(() => import("@/pages/AuthLogout/auth-logout"));
const SsoLogin = lazy(() => import("@/pages/SsoLogin"));
const LayoutLogin = lazy(() => import("@/components/layout-login"));
const LayoutMain = lazy(() => import("@/pages/layout-main"));
const Home = lazy(() => import("@/pages/Home"));
const ContentValidatorHuman = lazy(() => import("@/pages/ContentValidatorHuman"));
const VccImport = lazy(() => import("@/pages/VccImport"));
const Upload = lazy(() => import("@/pages/Upload"));
const ValidateResult = lazy(() => import("@/pages/VccImport/ValidateResult"));
const TechConnect = lazy(() => import("@/pages/TechConnect"));

const Analytics = lazy(() => import("@/pages/Dashboard"));
const RedirectingPage = lazy(() => import("@/pages/Redirecting/RedirectingPage"));

export const router = createBrowserRouter(
  [
    // Public routes — share the login page layout
    {
      element: (
        <Suspense fallback={<Loading />}>
          <LayoutLogin />
        </Suspense>
      ),
      children: [
        {
          path: ROUTE_NAME.AUTH_LOGIN,
          element: (
            <Suspense fallback={<Loading />}>
              <AuthLogin />
            </Suspense>
          ),
        },
        {
          path: ROUTE_NAME.AUTH_FORGOT,
          element: (
            <Suspense fallback={<Loading />}>
              <AuthForgot />
            </Suspense>
          ),
        },
        {
          path: ROUTE_NAME.AUTH_UPDATE,
          element: (
            <Suspense fallback={<Loading />}>
              <AuthUpdate />
            </Suspense>
          ),
        },
        {
          path: ROUTE_NAME.AUTH_LOGOUT,
          element: (
            <Suspense fallback={<Loading />}>
              <AuthLogout />
            </Suspense>
          ),
        },
        {
          path: ROUTE_NAME.SSO_LOGIN,
          element: (
            <Suspense fallback={<Loading />}>
              <SsoLogin />
            </Suspense>
          ),
        },
      ],
    },

    // Protected routes with layout
    {
      element: <ProtectedRoute />,
      children: [
        {
          element: (
            <Suspense fallback={<Loading />}>
              <LayoutMain />
            </Suspense>
          ),
          children: [
            {
              index: true, // Default route - redirects "/" to "/dashboard"
              element: <Navigate to={`/${ROUTE_NAME.DASHBOARD}`} replace />,
            },
            {
              path: ROUTE_NAME.DASHBOARD,
              element: <Home />,
            },
            {
              path: ROUTE_NAME.CONTENT_VALIDATOR_HUMAN,
              children: [
                {
                  index: true,
                  element: <ContentValidatorHuman />,
                },
                {
                  path: ROUTE_NAME.VCC_IMPORT,
                  element: <VccImport />,
                },
                {
                  path: ROUTE_NAME.UPLOAD,
                  element: <Upload />,
                },
                {
                  path: ROUTE_NAME.VALIDATE_RESULT,
                  element: <ValidateResult />,
                },
              ],
            },

            {
              path: ROUTE_NAME.CONTENT_VALIDATOR_TECH_CONNECT,
              element: <TechConnect />,
            },
            {
              path: ROUTE_NAME.ANALYTICS,
              element: <Analytics />,
            },
            {
              path: `${ROUTE_NAME.REDIRECTING}/:destination`,
              element: <RedirectingPage />,
            },
            // Add more protected routes here
            {
              ...BILLING_ROUTE,
            },
            {
              ...KEYS_ROUTE,
            },
            {
              ...EVALUATION_ROUTE,
            },
          ],
        },
      ],
    },

    // Fallback redirect
    /*
  {
    path: "*",
    element: <Navigate to={`/${ROUTE_NAME.DASHBOARD}`} replace />,
  },
  */
  ],
  { basename: "/aicv-bo" },
);
